import {
    TemplateComponent,
    html,
} from '/webresources-sources/js/components/template-component';
import ExpiryStorage from '/webresources-sources/js/commons/ExpiryStorage';
import { defineComponent } from '/webresources-sources/js/util/jquery';

export default class CatalogCart extends TemplateComponent {
    properties() {
        return {
            dataVariant: 'form', // "form" or "summary"
            dataHeadline: '',
            dataText: '',
            dataOrderLabel: '',
            dataShowSelectedText: '',
            dataEmptyText: '',
            dataEmptyLink: '',
            dataEmptyLinkLabel: '',
            dataFormUrl: '',
            catalogs: [],
        };
    }

    connected() {
        this.expiryStorage = new ExpiryStorage(60 * 60 * 24);

        this.requestUpdate();

        // force next animation frame because catalog-teaser components might not be defined yet...
        setTimeout(() => {
            this.catalogs = [...this.expiryStorage.getItem('requestedCatalogs', [])];
        });

        document.addEventListener(
            'scroll',
            (e) => {
                const elementPosition = this.element.offsetTop;
                const scrollPosition = window.scrollY;
                const isSticky = elementPosition <= scrollPosition;
                this.element.classList.toggle('is-sticky', isSticky);
            },
            { passive: true },
        );

        this.calculateCSSProperties();
    }

    watch() {
        return {
            catalogs: () => {
                this.dispatch('catalog-cart-update', { catalogs: this.catalogs });
                this.expiryStorage.setItem('requestedCatalogs', this.catalogs);
                this.calculateCSSProperties();
            },
        };
    }

    events() {
        return {
            document: {
                'add-catalog': (e) => {
                    this.catalogs = [...this.catalogs, e.detail];
                },
                'remove-catalog': (e) => {
                    this.catalogs = this.catalogs.filter((catalog) => `${catalog.id}` !== `${e.detail.id}`);
                },
            },
            '[data-show-selected-catalogs]': {
                click: () => {
                    this.element.classList.toggle('is-sticky', false);
                },
            },
            '[data-remove-catalog]': {
                click: (e) => {
                    const id = e.target.closest('[data-remove-catalog]').getAttribute('data-id');
                    const title = e.target.closest('[data-remove-catalog]').getAttribute('data-title');
                    this.catalogs = this.catalogs.filter((catalog) => `${catalog.id}` !== `${id}`);
                    this.dispatch('remove-catalog', { id, title });
                },
            },
        };
    }

    calculateCSSProperties() {
        const elementHeight = this.element.clientHeight;
        const selectedCatalogs = this.element.querySelector('.selected-catalogs');
        const selectedCatalogsHeight = selectedCatalogs?.clientHeight || 0;
        this.element.style = `--catalog-cart-height: ${elementHeight}px; --catalog-cart-selected-height: ${selectedCatalogsHeight}px;`;
    }

    template() {
        return this.dataVariant === 'form' ? this.templateForm() : this.templateSummary();
    }

    templateForm() {
        return html`
            <div class="catalog-cart-content bg-dark p-24 h-100 d-flex flex-column justify-content-between">
                <div class="row">
                    <div class="col col-12 col-md-8">
                        <div class="d-flex align-items-center mb-16">
                            <div class="h3 mb-0">${this.dataHeadline}</div>
                            <button
                                class="show-selected-catalogs-btn show-selected-catalogs-btn-md btn btn-link link link-icon align-items-center ml-32"
                                data-show-selected-catalogs
                            >
                                <span class="show-filter mr-4">${this.dataShowSelectedText}</span>
                            </button>
                        </div>
                        <div class="row selected-catalogs">
                            <div class="col col-12 col-md-8 pt-16 pt-md-32">
                                ${this.catalogs && this.catalogs.length > 0
                                    ? html`<ul class="list-unstyled">
                                          ${this.catalogs
                                              .map(
                                                  (catalog) => html`
                                                      <li
                                                          class="d-flex align-items-center justify-content-between mb-16"
                                                      >
                                                          <span>${catalog.title}</span>
                                                          <button
                                                              class="btn bnt-icon p-0"
                                                              data-remove-catalog
                                                              data-id="${catalog.id}"
                                                              data-title="${catalog.title}"
                                                          >
                                                              <svg
                                                                  width="16"
                                                                  height="16"
                                                                  viewBox="0 0 16 16"
                                                                  fill="none"
                                                                  xmlns="http://www.w3.org/2000/svg"
                                                              >
                                                                  <path
                                                                      d="M1 1L15 15M15 1L1 15"
                                                                      stroke="white"
                                                                      stroke-width="1.5"
                                                                  />
                                                              </svg>
                                                          </button>
                                                      </li>
                                                  `,
                                              )
                                              .join('')}
                                      </ul> `
                                    : html` <div>${this.dataText}</div>`}
                            </div>
                        </div>
                    </div>
                    <div class="col col-12 col-md-4 d-md-flex align-items-end justify-content-end">
                        <a
                            href="${this.dataFormUrl}"
                            class="btn btn-primary d-block ${this.catalogs.length > 0 ? '' : 'disabled'}"
                        >
                            ${this.dataOrderLabel}
                        </a>
                    </div>
                    <div class="col col-12 d-md-none">
                        <button class="show-selected-catalogs-btn show-selected-catalogs-btn-sm btn btn-link link link-icon w-100 align-items-center justify-content-center" data-show-selected-catalogs>
                            <span class="show-filter mr-4">${this.dataShowSelectedText}</span>
                        </button>
                    </div>
                </div>
            </div>
        `;
    }

    templateSummary() {
        if (!this.catalogs.length > 0) {
            return html`
                <div class="bg-light p-24" style="color: #d83541 !important;">
                    <input type="hidden" name="no-catalogs" required />
                    <p>${this.dataEmptyText}</p>
                    <a href="${this.dataEmptyLink}" class="link">${this.dataEmptyLinkLabel}</a>
                </div>
            `;
        }
        return html`
            <div class="bg-dark p-24 h-100">
                <div class="h3 mb-32">${this.dataHeadline}</div>
                <ul class="list-unstyled">
                    ${this.catalogs
                        .map(
                            (catalog, index) => html`
                                <li class="d-flex align-items-center justify-content-between mb-16">
                                    <span>${catalog.title}</span>
                                    <input
                                        type="hidden"
                                        id="catalogId${index + 1}"
                                        name="catalogId${index + 1}"
                                        value="${catalog.id}"
                                    />
                                    <input
                                        type="hidden"
                                        id="catalogTitle${index + 1}"
                                        name="catalogTitle${index + 1}"
                                        value="${catalog.title}"
                                    />
                                    <button
                                        class="btn bnt-icon p-0"
                                        data-remove-catalog
                                        data-id="${catalog.id}"
                                        data-title="${catalog.title}"
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M1 1L15 15M15 1L1 15" stroke="white" stroke-width="1.5" />
                                        </svg>
                                    </button>
                                </li>
                            `,
                        )
                        .join('')}
                </ul>
            </div>
        `;
    }
}

defineComponent('catalog-cart', CatalogCart);
